import {
  AttributeOptionsWithValue,
  GroupLevelAttribute,
  GroupLevelAttributeTemplate,
  GroupLevelAttributeValue
} from '@/jbi-shared/types/jaas-group-level-attributes.types';
import { VerifiedUsers } from '@/jbi-shared/types/user.types';
import { ApiState, SortOrder } from '@/store/types/general.types';
import { IPaginationOptions } from 'nestjs-typeorm-paginate';
import {
  GroupUserAttributeType,
  MyjbiGroup,
  MyjbiGroupDetail,
  MyjbiGroupMember,
  MyjbiGroupStat,
  MyjbiGroupUserAttribute,
  MyjbiGroupUserAttributeSpec
} from '../../../../jbi-shared/types/myjbi-group.types';
import { GetThirdPartyUsersResponsePayload } from '../../../../views/AdminThirdPartyManagement/types/admin-third-party-management.types';
import {
  Application,
  Product
} from '../../application/types/application.types';
import { Profile } from '../../profile/profile.state';
import {
  AddressCountry,
  AddressState,
  CreateGroupLevelAttributeResponse,
  CreateGroupLevelAttributeTemplateResponse,
  CurrencyOptions,
  GroupLevelAttributeType,
  GroupTemplate,
  LocationType,
  UpdateGroupLevelAttributeResponse,
  UpdateGroupLevelAttributeTemplateResponse,
  VenueFieldData
} from './group-level-attribute.types';
import { GetGroupsLicenseResponsePayload } from './group-license.types';
import { GetGroupsProductResponsePayload } from './group-product.types';
import { GroupUserAttributeTemplateUpStream } from './group-user-attribute-template.types';
import {
  GetGroupUserAttributeListResponse,
  GetUploadImageSignedUrlResponse,
  GroupUserAttributeWithGroupCount,
  NewGroupUserAttributeResponse,
  UpdateGroupsUserAttributeResponse
} from './group-user-attribute.types';
import {
  JbicChangeRequest,
  JbicGroups,
  MasterGroupsResponseDTO,
  ProtectedGroupTypes
} from './group.types';

export interface AdminApiState {
  inviteNewUserToProduct: ApiState;
  bulkInviteNewUserToProduct: ApiState;
  generateInvitationLinks: ApiState;
  getUsers: ApiState;
  toggleUser: ApiState;
  verifyUser: ApiState;
  removeUserInvitation: ApiState;
  removeInvitation: ApiState;
  getUserInvitations: ApiState;
  resendUserInvitation: ApiState;
  getTermsAndConditions: ApiState;
  getTermsAndConditionsApplication: ApiState;
  getTermsAndConditionsHistory: ApiState;
  addTermsAndConditions: ApiState;
  updateTermsAndConditions: ApiState;
  deleteTermsAndConditions: ApiState;
  setDefaultTermsAndConditions: ApiState;
  getInvitations: ApiState;
  getSubscriptions: ApiState;
  updateSubscriptionStatus: ApiState;
  createNewGroup: ApiState;
  getGroups: ApiState;
  getSpecificGroups: ApiState;
  getSpecificJBICGroups: ApiState;
  getSimplifiedGroups: ApiState;
  getGroupsFromAttribute: ApiState;
  getGroupsByUserAttribute: ApiState;
  getHighestDirectActiveGroupsByUserAttribute: ApiState;
  getSubGroups: ApiState;
  getMembers: ApiState;
  getGroupAttributeTypes: ApiState;
  getProtectedGroupTypes: ApiState;
  getGroupDetail: ApiState;
  getParentGroupDetailByParentGroupId: ApiState;
  getMasterGroupEmailDomains: ApiState;
  getParentGroupDetailByChildGroupId: ApiState;
  addMembersToGroup: ApiState;
  editGroupMember: ApiState;
  updateGroup: ApiState;
  removeEmailDomain: ApiState;
  deleteGroupMember: ApiState;
  deleteGroup: ApiState;
  getGroupProducts: ApiState;
  createGroupProduct: ApiState;
  updateGroupProduct: ApiState;
  createGroupLicense: ApiState;
  getGroupStats: ApiState;
  getGroupLicenses: ApiState;
  updateGroupLicense: ApiState;
  deleteGroupLicense: ApiState;
  resendEmailNotificationToGroupMember: ApiState;
  exportMembersListToCsv: ApiState;
  exportDeleteMembersListToCsv: ApiState;
  uploadUserListFile: ApiState;
  validateSingleUserProduct: ApiState;
  getGroupAttributeOrder: ApiState;
  getThirdPartyUsers: ApiState;
  createThirdPartyUser: ApiState;
  getGroupTypes: ApiState;
  groupType: ApiState;
  updateThirdPartyUser: ApiState;
  getMembersCountByGroupId: ApiState;
  getUserByEmail: ApiState;
  updateUserByAdmin: ApiState;
  getUserByAdmin: ApiState;
  getGroupLevelAttributeTypes: ApiState;
  getAllGroupLevelAttributes: ApiState;
  getPaginatedGroupLevelAttributes: ApiState;
  getDefaultGroupLevelAttributes: ApiState;
  getGroupLevelAttributesFromTemplateByGroupType: ApiState;
  getGroupLevelAttributeById: ApiState;
  createGroupLevelAttribute: ApiState;
  deleteGroupLevelAttribute: ApiState;
  updateGroupLevelAttribute: ApiState;
  verifyGroupLevelAttribute: ApiState;
  getGroupLevelAttributeValuesByGroupId: ApiState;
  createGroupLevelAttributeValues: ApiState;
  updateGroupLevelAttributeValues: ApiState;
  upsertIndividualGroupLevelAttributeValue: ApiState;
  deleteGroupLevelAttributeValues: ApiState;
  getGroupLevelAttributeTemplates: ApiState;
  getGroupLevelAttributeTemplateById: ApiState;
  createGroupLevelAttributeTemplate: ApiState;
  updateGroupLevelAttributeTemplate: ApiState;
  deleteGroupLevelAttributeTemplate: ApiState;
  verifyGroupLevelAttributeTemplateName: ApiState;
  createGroupTemplate: ApiState;
  getGroupTemplates: ApiState;
  getGroupTemplateById: ApiState;
  updateGroupTemplateApi: ApiState;
  deleteGroupTemplate: ApiState;
  verifyGroupTemplate: ApiState;
  verifyGroupUserAttribute: ApiState;
  createGroupUserAttribute: ApiState;
  updateGroupUserAttribute: ApiState;
  deleteGroupUserAttribute: ApiState;
  updateGroupsWithAttribute: ApiState;
  getSpecsByAttributeId: ApiState;
  getGroupUserAttributeTypes: ApiState;
  getGroupUserAttributeById: ApiState;
  getGroupUserAttributes: ApiState;
  getGroupUserAttributeUploadImageSignedUrl: ApiState;
  getCurrentUserUploadImageSignedUrl: ApiState;
  getCurrentUserAttributes: ApiState;
  updateCurrentUserAttributeValues: ApiState;
  getUserGroupsByUserId: ApiState;
  getGroupUserAttributeList: ApiState;
  getCountries: ApiState;
  getCountryById: ApiState;
  getStatesByCountryId: ApiState;
  getGroupUserAttributeTemplates: ApiState;
  verifyGroupUserAttributeTemplateName: ApiState;
  createGroupUserAttributeTemplate: ApiState;
  deleteGroupUserAttributeTemplate: ApiState;
  updateGroupUserAttributeTemplate: ApiState;
  getFilterUsers: ApiState;
  getFilterVerifiedUsers: ApiState;
  getFilterGroups: ApiState;
  getAllProtectedMasterGroups: ApiState;
  getJbicGroupsWithPagination: ApiState;
  getJbicEventsWithPagination: ApiState;
  getJbicEventById: ApiState;
  getJbicGroupEventsWithPagination: ApiState;
  createEvent: ApiState;
  updateEvent: ApiState;
  createImage: ApiState;
  updateImageDetails: ApiState;
  getJbicGroupImagesWithPagination: ApiState;
  getJbicGroupChangeRequestCounts: ApiState;
  getJbicChangeRequestImagesWithPagination: ApiState;
  getJbicChangeRequestEventsWithPagination: ApiState;
  deleteJbicEvent: ApiState;
  deleteJbicImage: ApiState;
  submitEventForApproval: ApiState;
  updateManagedEventStatus: ApiState;
  updateImageStatusSubmission: ApiState;
  updateManagedImageStatus: ApiState;
  getEditedEvents: ApiState;
  getEditedImages: ApiState;
}

export interface EditedEvents {
  event: Event;
  lastReviewEvent: EventVersion | null;
}

export interface EditedImages {
  images: JbicImage[];
  lastReviewImages: JbicImageVersion[] | null;
}

export interface EditedImage {
  images: JbicImage;
  lastReviewImages: JbicImageVersion | null;
}

export interface Invitation {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  applicationProductId: number;
  token: string;
  createdAt: string;
  expiredAt: string;
  acceptedAt: string | null;
  lastInvitedAt: string | null;
  applicationProduct: Product;
}

export interface User {
  userId: number;
  email: string;
  emailVerified: boolean;
  firstName: string;
  lastName: string;
  type: string;
  status: string;
  username: string;
}

export interface UserWithExtraAttributes extends User {
  [s: string]: any;
}

export interface Pagination<T> {
  itemCount: number;
  items: T[];
  next: string;
  pageCount: number;
  previous: string;
  totalItems: number;
}

export interface AdminState {
  apiState: AdminApiState;
  userInvitations: Pagination<Invitation> | undefined;
  userDetails: Pagination<User> | undefined;
  invitations: Invitation[] | undefined;
  subscriptions: any[] | undefined;
  termsAndConditionsPayload: Pagination<TermsAndConditions> | undefined;
  termsAndConditionsApplicationPayload: TermsAndConditions | undefined;
  termsAndConditionsHistoryPayload: TermsAndConditions | undefined;
  groups: GetGroupsResponsePayload | undefined;
  specificGroups: Group[] | undefined;
  specificJBICGroups: Group[] | undefined;
  simplifiedGroups: Group[] | undefined;
  groupsFromAttribute: GetGroupsResponsePayload | undefined;
  subGroups: GetSubGroupsResponsePayload | undefined;
  protectedGroupTypes: GetProtectedGroupTypesResponsePayload | undefined;
  groupDetail: GroupDetail | undefined;
  members: Pagination<MyjbiGroupMember> | undefined;
  parentGroupDetailByParentGroupId: MyjbiGroupDetail | undefined;
  parentGroupDetailByChildGroupId: MyjbiGroupDetail | undefined;
  groupProducts: GetGroupsProductResponsePayload | undefined;
  groupStats: GetGroupStatsResponsePayload | undefined;
  groupLicenses: GetGroupsLicenseResponsePayload | undefined;
  uploadUserListFile: any | undefined; // change payload to URL returned
  validateSingleUserProduct: InviteUserToProductPayload | undefined;
  groupAttributeOrder: GroupAttributeOrderResponsePayload | undefined;
  groupProductsNameList: string[] | undefined;
  groupList: string[] | undefined;
  masterGroupEmailDomains: string[] | undefined;
  userByEmailResponse: UserByEmailResponsePayload | undefined;
  thirdPartyUsers: GetThirdPartyUsersResponsePayload | undefined;
  groupTypes: Pagination<GroupType> | undefined;
  userProfile: Profile | null;
  groupLevelAttributeTypes: GroupLevelAttributeType[] | undefined;
  allGroupLevelAttributes: GroupLevelAttribute[] | undefined;
  paginatedGroupLevelAttributes: Pagination<GroupLevelAttribute> | undefined;
  defaultGroupLevelAttributes: GroupLevelAttribute[] | undefined;
  groupLevelAttributesFromTemplateByGroupType:
    | GroupLevelAttribute[]
    | undefined;
  groupLevelAttribute: GroupLevelAttribute | undefined;
  newGroupLevelAttribute: CreateGroupLevelAttributeResponse | undefined;
  updatedGroupLevelAttribute: UpdateGroupLevelAttributeResponse | undefined;
  verifyGroupLevelAttribute: boolean | undefined;
  groupLevelAttributeValuesByGroupId: GroupLevelAttributeValue[] | undefined;
  groupLevelAttributeTemplates:
    | Pagination<GroupLevelAttributeTemplate>
    | undefined;
  groupLevelAttributeTemplate: GroupLevelAttributeTemplate | undefined;
  verifyGroupLevelAttributeTemplateName: boolean | undefined;
  newGroupLevelAttributeTemplate:
    | CreateGroupLevelAttributeTemplateResponse
    | undefined;
  updatedGroupLevelAttributeTemplate:
    | UpdateGroupLevelAttributeTemplateResponse
    | undefined;
  updatedGroupLevelAttributeValue?: AttributeOptionsWithValue;
  createdGroupLevelAttributeValues?: AttributeOptionsWithValue[];
  groupTemplateById: GroupTemplate | undefined; // used to store created and updated result
  groupTemplates: Pagination<GroupTemplate> | undefined;
  verifyGroupTemplate: boolean | undefined;
  verifyGroupUserAttribute: boolean | undefined;
  groupsByUserAttribute: GetGroupsResponsePayload | undefined;
  highestDirectActiveGroupsByUserAttribute: Group[] | undefined;
  groupUserAttributeTypes: GroupUserAttributeType[] | undefined;
  groupUserAttributeById: GroupUserAttributeWithGroupCount | undefined;
  groupUserAttributes: MyjbiGroupUserAttribute[] | undefined;
  newGroupUserAttribute: NewGroupUserAttributeResponse | undefined;
  updatedGroupUserAttribute: UpdateGroupsUserAttributeResponse | undefined;
  groupUserAttributeUploadImageSignedUrl:
    | GetUploadImageSignedUrlResponse
    | undefined;
  currentUserUploadImageSignedUrl: GetUploadImageSignedUrlResponse | undefined;
  groupUserAttributeList: GetGroupUserAttributeListResponse | undefined;
  currentUserAttributes: CurrentUserAttribute[] | undefined;
  getUserGroups: object | undefined;
  attributeSpecs: MyjbiGroupUserAttributeSpec[] | undefined;
  countries: AddressCountry[] | undefined;
  currencies: CurrencyOptions[] | undefined;
  countryById: AddressCountry | undefined;
  statesByCountryId: AddressState[] | undefined;
  groupUserAttributeTemplateExisted: boolean | undefined;
  groupUserAttributeTemplates:
    | Pagination<GroupUserAttributeTemplateUpStream>
    | undefined;
  newGroupUserAttributeTemplateId: number | undefined;
  filteredUsers: User[] | undefined;
  filteredVerifiedUsers: VerifiedUsers[] | undefined;
  filteredGroups: Group[] | undefined;
  masterGroups: MasterGroupsResponseDTO[] | undefined;
  currentGroupName?: string;
  jbicGroupsWithPagination: Pagination<JbicGroups> | undefined;
  jbicEventsWithPagination: Pagination<Event> | undefined;
  jbicEvent: Event | undefined;
  jbicGroupEventsWithPagination: Pagination<Event> | undefined;
  jbicGroupImagesWithPagination: Pagination<JbicImage> | undefined;
  jbicChangeRequestCounts: JbicChangeRequest[] | undefined;
  jbicChangeRequestImagesWithPagination: Pagination<JbicImage> | undefined;
  jbicChangeRequestEventsWithPagination: Pagination<Event> | undefined;
  editedEvents: EditedEvents | undefined;
  editedImages: EditedImages | undefined;
}

export interface GroupType {
  id: number;
  name: string;
  status: string;
  createdAt: string;
  updatedDate: string;
  masterGroupCount: number;
  subGroupCount: number;
}

export interface UserProductsInvite {
  productId: number;
  productName: string;
  redeemable: number;
}

export type CheckProductStatus = UserProductsInvite & { productStatus: string };
export type JobResultStatus = InviteUserToProductPayload[] | JobFailureMessage;
export interface InviteUserToProductPayload {
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  productId: number;
  products?: UserProductsInvite[];
}

export interface JobFailureMessage {
  jobId: number;
  status: string;
  message: string;
}

export interface DifferenceInviteUserToProductPayload {
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  productId?: number;
  products?: UserProductsInvite[];
}

export interface BulkInviteUserToProductPayload {
  invitations: InviteUserToProductPayload[];
}

export interface GetInvitedUserPayload {
  username?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  selectedStatus?: string;
  userType?: string;
  page: number;
  limit: number;
  sortOrder?: 'ASC' | 'DESC';
  sortColumn?: string;
  domains?: string[];
}
export interface GetFilteredUserListPayload {
  searchInput: string;
  filterBy: string;
  page?: number;
  limit?: number;
}

export interface GetFilteredVerifiedUserListPayload {
  searchInput: string;
  page?: number;
  limit?: number;
}

export interface GetFilteredGroupListPayload {
  groupName: string;
  groupType: string;
  page?: number;
  limit?: number;
}

export type GetInvitedUserPayloadSort = Required<
  Pick<GetInvitedUserPayload, 'sortColumn' | 'sortOrder'>
>;

export interface FilteredUserPayload {
  username?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  selectedStatus?: string;
  selectedType?: string;
  perPage?: number;
  page?: number;
}

export interface GetInvitationsPayload {
  email?: string;
  latestActivity?: string;
  applicationId?: string;
  invitationStatus?: string;
  page?: number;
  limit?: number;
  sortOrder?: 'ASC' | 'DESC';
  sortColumn?: string;
}

export interface GetUserInvitationHistoryPayload {
  email: string;
  page: number;
  limit: number;
  sortColumn: string;
  sortOrder: 'ASC' | 'DESC';
}

export type GetInvitationsPayloadSort = Required<
  Pick<GetInvitationsPayload, 'sortColumn' | 'sortOrder'>
>;

export type InvitationsTabFilterPayload = Required<
  Pick<
    GetInvitationsPayload,
    'email' | 'applicationId' | 'latestActivity' | 'invitationStatus'
  >
>;

export interface UserDetail {
  blocked: boolean;
  createdAt: string;
  createdBy: null;
  email: string;
  emailVerified: boolean;
  id: number;
  passwordHash: string;
  updatedAt: string;
  updatedBy: null;
  userProfile: UserProfile;
  username: string;
}

export interface UserProfile {
  birthdate: null;
  country: null;
  createdAt: string;
  createdBy: null;
  firstName: string;
  gender: null;
  id: number;
  lastName: string;
  organisation: null;
  phoneNumber: null;
  researchgate: null;
  shortbio: null;
  source: null;
  specialty: null;
  updatedAt: string;
  updatedBy: null;
  username: string;
}

export interface TermsAndConditions {
  id: number;
  title: string;
  content: string;
  tncStatus: string;
  createdAt: string;
}
export interface GetTermsAndConditionsPayload extends IPaginationOptions {
  sortOrder?: 'ASC' | 'DESC';
  sortColumn?: string;
  application?: string;
  status?: string;
}

export type GetTermsAndConditionsPayloadSort = Required<
  Pick<GetTermsAndConditionsPayload, 'sortColumn' | 'sortOrder'>
>;

export interface AddTermsAndConditionPayload {
  title: number;
  content: string;
  tncStatus: boolean;
  application: string;
}

export interface UpdateTermsAndConditionPayload {
  id: number;
  title: number;
  content: string;
  tncStatus: boolean;
  appId: string;
}

export interface FilteredTermsAndConditionPayload {
  application: string;
  status: string;
}

interface GroupLicenseStatus {
  groupLicenseId: number;
  isLicenseExpired: boolean;
  name: string;
  status: string;
}

export type Group = MyjbiGroup & {
  endAt: Date;
  types: GroupType;
  protectedType?: ProtectedGroupTypes;
  groupLicenseStatus: GroupLicenseStatus;
};

export type FlatGroup = MyjbiGroup & {
  types: string[];
  groupId: number;
  protectedType?: ProtectedGroupTypes;
  groupLicenseStatus: GroupLicenseStatus;
};

export type CurrentUserAttribute = MyjbiGroupUserAttributeSpec & {
  groupId: number;
  groupName: string;
  groupPath: string;
};

export interface ExtendedFilteredGroupPayload extends FilteredGroupPayload {
  groupId: number;
}

export type GetGroupsResponsePayload = Pagination<Group>;

export type GetSubGroupsResponsePayload = Pagination<Group>;

export type GetMembersResponsePayload = Pagination<MyjbiGroupMember>;

export type GetGroupStatsResponsePayload = MyjbiGroupStat[];

export type GetProtectedGroupTypesResponsePayload = ProtectedGroupTypes[];

export interface FilteredGroupPayload {
  groupname?: string;
  grouptype?: string;
  sortOrder?: 'ASC' | 'DESC';
  sortColumn?: string;
  page: number;
  limit?: number;
  attributeId?: number | null;
  scope?: GROUP_LISTING_SCOPE;
}

export interface SimplifiedGroupsPayload {
  query: string;
  groupType?: string;
}

export interface FilteredSubGroupPayload {
  nlevel: number;
  sortOrder?: 'ASC' | 'DESC';
  sortColumn?: string;
  page: number;
  limit: number;
}

export type GetGroupFilterPayload = Pick<
  FilteredGroupPayload,
  'groupname' | 'grouptype'
>;

export interface FilteredGetGroup {
  groupname?: string;
  grouptype?: string;
}

export interface FilteredGroupProductsPayload {
  productName?: string;
}

export interface ExportGroupMembersCsvPayload {
  id: number;
  parentsName: string[];
  selectedAttributes?: string[];
}
export interface ExportDeleteGroupMembersCsvPayload {
  groupId: number;
  parentsName: string[];
  emailDomain: string;
  allDomains: string[];
  isFirstDomain: boolean;
}
export interface ValidateUserListPayload {
  fileLink: string;
  emailIndex: number;
  userAttributesSpecs: MyjbiGroupUserAttributeSpec[];
  emailDomains: string[];
}

interface GroupDetailAddOns {
  parentsName: string[];
}

export type GroupDetail = MyjbiGroupDetail & GroupDetailAddOns;

export interface GetGroupProductsPayload {
  productName?: string | undefined;
  page?: number;
  limit?: number;
  sortOrder?: 'ASC' | 'DESC';
  sortColumn?: string;
}

export type GetGroupProductsFilterPayload = Pick<
  GetGroupProductsPayload,
  'productName'
>;

export type GetGroupProductsPayloadSort = Required<
  Pick<GetGroupProductsPayload, 'sortColumn' | 'sortOrder'>
>;

export interface UserTabFilterPayload {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  selectedUserType: string;
  selectedStatus: string;
}

export interface ValidateUserInvited {
  emailIndex: number;
  fileLink: string;
}

export interface GroupAttributeOrder {
  groupId: number;
  attributeOrder: string[];
}
export interface GroupAttributeOrderResponsePayload {
  user: User;
  group: Group;
  id: number;
  groupId: number;
  userId: number;
  attributeOrder: string[];
  createdDate: Date;
  updatedDate: Date;
}
export interface UserByEmailResponsePayload {
  userExist: boolean;
  user?: {
    username: string;
    userId: number;
    firstName: string;
    lastName: string;
    userEmail: string;
  };
}
export interface TermsAndConditionsValues {
  id?: number;
  title: string;
  content: string;
  tncStatus: string | boolean;
  application: string | Application;
  tncParentId?: number;
  isRestoredVersion?: boolean;
  isCurrent?: boolean;
  createdAt?: Date;
}

export enum TermsAndConditionsStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
  DELETED = 'Deleted'
}
export interface TermsAndConditionEditPayload {
  isTncEdited: boolean;
  title: string;
}

export interface SelectGroupFilterParam {
  selectedType: string;
  filterInput: string;
}

export interface AttributeTypeOption {
  id: number;
  name: string;
  slug: string;
}

/** This enum is added to check scope of GROUPs API to restrict user's groups access  */
export enum GROUP_LISTING_SCOPE {
  ADD_USER_ATTRIBUTE_TO_GROUP = 'update_groups-update_user_attributes-create_existing_user_attributes',
  REMOVE_USER_ATTRIBUTE_FROM_GROUP = 'update_groups-update_user_attributes-delete_added_user_attributes',
  CREATE_NEW_ATTRIBUTE = 'update_groups-update_user_attributes-create_new_user_attributes'
}

export enum SELECT_GROUP_STEP_CONTEXT {
  REMOVE_FROM_GROUP = 'REMOVE',
  ADD_GROUPS = 'ADD',
  MANAGE_SETTINGS = 'SETTINGS'
}

export const initQueryParams = (): FilteredGroupPayload => ({
  groupname: '',
  grouptype: '',
  sortOrder: 'ASC',
  sortColumn: 'name',
  page: 1,
  limit: 50,
  attributeId: null,
  scope: GROUP_LISTING_SCOPE.ADD_USER_ATTRIBUTE_TO_GROUP
});

export enum EventStatus {
  DRAFT = 'DRAFT',
  AWAITING_APPROVAL = 'AWAITING_APPROVAL',
  DECLINED = 'DECLINED',
  APPROVED = 'APPROVED',
  PUBLISHED = 'PUBLISHED'
}

export interface EventValue {
  title: string;
  slogan: string;
  startAt: string;
  endAt: string;
  timezone: string;
  description: string;
  location: JbicLocationDetails;
  urlToEvent: string;
  urlToHost: string;
  contacts: EventContact[];
  bannerImageUrl: string;
  teaserImageUrl: string;
}

export interface Event extends EventValue {
  id: number;
  status: EventStatus;
  group: Group;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  createdBy: number;
  isEnded: boolean;
  declinedReason?: string;
}

export interface EventVersion extends Event {
  eventId: number;
}

export interface CreateEventDTO {
  isDraft: boolean;
  groupId: number;
  status?: EventStatus;
  title: string;
  slogan: string;
  startAt: string;
  endAt: string;
  timezone: string;
  description: string;
  location: JbicLocationDetails;
  urlToEvent: string;
  urlToHost: string;
  contacts: EventContact[];
  bannerImageUrl: string;
  teaserImageUrl: string;
  declinedReason?: string | null;
}

export interface JbicLocationDetails {
  locationType: LocationType;
  venueDetails?: VenueFieldData;
  remoteLink?: string;
}

export interface EventContact {
  name: string;
  role: string;
  phoneNumber: string;
}

export interface JbicEventParams {
  groupName?: string;
  eventName?: string;
  status?: string;
  dates?: string[];
  page?: number;
  limit?: number;
  sortColumn?: string;
  sortOrder?: SortOrder;
}

export enum ImageStatus {
  DRAFT = 'DRAFT',
  AWAITING_APPROVAL = 'AWAITING_APPROVAL',
  DECLINED = 'DECLINED',
  APPROVED = 'APPROVED',
  PUBLISHED = 'PUBLISHED'
}

export interface JbicImage {
  id: number;
  imageUrl: string;
  alternateText: string;
  caption: string;
  status: ImageStatus;
  groupId: number;
  createdAt: Date;
  createdBy: User;
  deletedAt?: Date;
  declinedReason?: string;
}

export interface JbicImageVersion extends JbicImage {
  imageId: number;
}

export interface JbicGroupImageFilterOptions {
  groupId?: number;
  status?: ImageStatus;
  search?: string;
  page?: number;
  limit?: number;
  sortColumn?: string;
  sortOrder?: SortOrder;
}

export interface JbicImageChangeRequestFilterOptions {
  status?: ImageStatus;
  requestedOn?: Date;
  requestedBy?: string;
  page: number;
  limit: number;
  sortColumn: string; // change to JbicImageSortColumn once sort columns confirmed
  sortOrder: SortOrder;
}

export interface JbicEventChangeRequestFilterOptions {
  status?: EventStatus;
  requestedOn?: Date;
  requestedBy?: string;
  page?: number;
  limit?: number;
  sortColumn?: string; // change to JbicEventSortColumn once sort columns confirmed
  sortOrder?: SortOrder;
}

export type CreateImageDTO = {
  imageUrl: string;
  alternateText: string;
  caption: string;
  status: ImageStatus;
  groupId: number;
};

export interface UpdateImageDetailsDTO {
  alternateText: string;
  caption: string;
  status: ImageStatus;
}

export interface UpdateJbicImage {
  imageId: number;
  alternateText: string;
  caption: string;
  status: ImageStatus;
}

export interface UpdateEventDTO {
  id: number;
  payload: CreateEventDTO;
}

export interface DeleteEventDTO {
  id: number;
}

export interface DeleteImageDTO {
  ids: number[];
}

export interface BulkUpdateImageStatusSubmissionDTO {
  ids: number[];
  status: ImageStatus;
}

export interface EditedImagesDTO {
  ids: number[];
}

export interface BulkUpdateImageStatusManageDTO {
  ids: number[];
  status: ImageStatus;
  declinedReason?: string;
}

export interface UpdateEventStatusManageDTO {
  status: EventStatus;
  declinedReason?: string;
}

export interface UpdateEventStatusSubmissionDTO {
  status: EventStatus;
  id: number;
}

export enum JbicEntityType {
  EVENT = 'EVENT',
  IMAGE = 'IMAGE'
}
